import { useEffect, useRef, useState } from "react";

import { FilterHit } from "./sharedTypes";

export type FetchFNResult = Array<FilterHit>;
export type FetchFNParams<TDeps> = { attributeName: string; query: string; signal: AbortSignal; deps: Required<TDeps> };

type Props<TDeps> = {
  query: string;
  deps: TDeps;
  attributeName: string;
  fetchFN?: (_: FetchFNParams<TDeps>) => Promise<FetchFNResult>;
};

const QUERY_DEBOUNCE_MS = 400;

type State = {
  availableFilterHits: FetchFNResult;
  loading: boolean;
  error: boolean;
};

/**
 * Get filter hits for attributeName and typed query
 */
export function useFilterHitsQuery<TDeps extends Array<string | undefined>>({
  attributeName,
  query,
  fetchFN,
  deps,
}: Props<TDeps>) {
  const fetchFNRef = useRef(fetchFN);
  const debouncedQuery = useDebounceValue(query, QUERY_DEBOUNCE_MS);

  const [state, setState] = useState<State>({
    availableFilterHits: [],
    loading: false,
    error: false,
  });

  useEffect(() => {
    if (!fetchFNRef || !fetchFNRef.current || !deps || !Boolean(deps[0])) return;

    const abortController = new AbortController();

    setState({
      loading: true,
      error: false,
      availableFilterHits: [],
    });

    fetchFNRef
      .current({
        query: debouncedQuery ?? "",
        signal: abortController.signal,
        attributeName,
        deps: deps as Required<TDeps>,
      })
      .then((availableFilters) => {
        if (abortController.signal.aborted) return;
        setState({
          loading: false,
          error: false,
          availableFilterHits: availableFilters,
        });
      })
      .catch((e) => {
        if (abortController.signal.aborted) return;
        setState({
          loading: false,
          error: !!e,
          availableFilterHits: [],
        });
      });

    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, attributeName, ...deps]);

  return {
    ...state,
  };
}

/**
 * Returns value once it stops changing for ms (initial value is returned immediately)
 */
function useDebounceValue<T>(value: T, ms: number) {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const nodeTimeout = setTimeout(() => {
      setDebouncedValue(value);
    }, ms);
    return () => {
      clearTimeout(nodeTimeout);
    };
  }, [value, ms]);

  return debouncedValue;
}
